import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../../services/api";
import "./subject-list-premium.css";

export default function SubjectList() {
  const schoolAdmin = JSON.parse(localStorage.getItem("schoolAdmin"));
  const schoolId = schoolAdmin.school.id;
  const navigate = useNavigate();

  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingSubject, setEditingSubject] = useState(null);
  const [editName, setEditName] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  const loadClasses = async () => {
    const res = await API.get("/school-admin/classes/", { headers: { "X-SCHOOL-ID": schoolId } });
    setClasses(res.data.classes || []);
  };

  const loadSections = async (cid) => {
    if (!cid) { setSections([]); return; }
    const res = await API.get(`/school-admin/sections/?class_id=${cid}`, { headers: { "X-SCHOOL-ID": schoolId } });
    setSections(res.data.sections || []);
  };

  const loadSubjects = async () => {
    setLoading(true);
    const params = {};
    if (classId) params.class_id = classId;
    if (sectionId) params.section_id = sectionId;
    if (search) params.search = search;
    const res = await API.get("/school-admin/subjects/", { headers: { "X-SCHOOL-ID": schoolId }, params });
    setSubjects(res.data.subjects || []);
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this subject?")) return;
    await API.delete(`/school-admin/subjects/delete/${id}/`, { headers: { "X-SCHOOL-ID": schoolId } });
    loadSubjects();
  };

  const handleUpdate = async () => {
    if (!editName.trim()) { alert("Subject name required"); return; }
    setEditLoading(true);
    try {
      await API.put(`/school-admin/subjects/update/${editingSubject.id}/`,
        { name: editName },
        { headers: { "X-SCHOOL-ID": schoolId } }
      );
      setEditingSubject(null);
      setEditName("");
      loadSubjects();
    } catch { alert("Update failed"); }
    setEditLoading(false);
  };

  useEffect(() => { loadClasses(); loadSubjects(); }, []);
  useEffect(() => { loadSubjects(); }, [classId, sectionId, search]);

  const groupedSubjects = Object.values(
    subjects.reduce((acc, s) => {
      const key = `${s.name}-${s.class_name}`;
      if (!acc[key]) acc[key] = { ...s, sections: [] };
      if (s.section_name && !acc[key].sections.includes(s.section_name))
        acc[key].sections.push(s.section_name);
      return acc;
    }, {})
  );

  const SUBJECT_COLORS = [
    "#6366f1","#ec4899","#f59e0b","#10b981","#3b82f6","#8b5cf6",
    "#ef4444","#06b6d4","#84cc16","#f97316",
  ];

  return (
    <div className="sb-page">

      {/* HERO */}
      <div className="sb-hero">
        <div className="sb-hero-left">
          <span className="sb-hero-icon">📖</span>
          <div>
            <h1 className="sb-hero-title">Subjects</h1>
            <p className="sb-hero-sub">{groupedSubjects.length} subjects found</p>
          </div>
        </div>
        <button className="sb-btn-white" onClick={() => navigate("/school-admin/subjects/add")}>
          + Add Subject
        </button>
      </div>

      {/* FILTERS */}
      <div className="sb-filters">
        <div className="sb-filter-field">
          <label>Class</label>
          <select value={classId} onChange={(e) => { setClassId(e.target.value); setSectionId(""); loadSections(e.target.value); }}>
            <option value="">All Classes</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="sb-filter-field">
          <label>Section</label>
          <select value={sectionId} disabled={!sections.length} onChange={(e) => setSectionId(e.target.value)}>
            <option value="">All Sections</option>
            {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="sb-filter-field sb-grow">
          <label>Search</label>
          <input type="text" placeholder="Search subject name..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {(classId || sectionId || search) && (
          <button className="sb-clear-btn" onClick={() => { setClassId(""); setSectionId(""); setSearch(""); setSections([]); }}>
            ✕ Clear
          </button>
        )}
      </div>

      {/* TABLE CARD */}
      <div className="sb-card">
        {loading ? (
          <div className="sb-state">⏳ Loading subjects…</div>
        ) : groupedSubjects.length === 0 ? (
          <div className="sb-state">
            <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
            <p>No subjects found</p>
          </div>
        ) : (
          <div className="sb-table-wrap">
            <table className="sb-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Subject</th>
                  <th>Class</th>
                  <th>Section</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupedSubjects.map((s, i) => (
                  <tr key={i}>
                    <td>
                      <span className="sb-num" style={{ background: SUBJECT_COLORS[i % SUBJECT_COLORS.length] + "22", color: SUBJECT_COLORS[i % SUBJECT_COLORS.length] }}>
                        {i + 1}
                      </span>
                    </td>
                    <td>
                      <div className="sb-subject-name">
                        <span className="sb-dot" style={{ background: SUBJECT_COLORS[i % SUBJECT_COLORS.length] }} />
                        <strong>{s.name}</strong>
                      </div>
                    </td>
                    <td><span className="sb-class-pill">{s.class_name}</span></td>
                    <td>
                      <span className="sb-section-pill">
                        {s.sections.length > 1 ? "All Sections" : s.sections.join(", ") || "—"}
                      </span>
                    </td>
                    <td className="sb-actions">
                      <button className="sb-edit-btn" onClick={() => { setEditingSubject(s); setEditName(s.name); }}>
                        ✏ Edit
                      </button>
                      <button className="sb-delete-btn" onClick={() => handleDelete(s.id)}>
                        🗑 Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* EDIT MODAL */}
      {editingSubject && (
        <div className="sb-overlay" onClick={() => setEditingSubject(null)}>
          <div className="sb-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sb-modal-icon">✏️</div>
            <h3 className="sb-modal-title">Edit Subject</h3>
            <p className="sb-modal-sub">Editing: <strong>{editingSubject.name}</strong></p>
            <input
              className="sb-modal-input"
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="New subject name"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleUpdate()}
            />
            <div className="sb-modal-row">
              <button className="sb-modal-cancel" onClick={() => setEditingSubject(null)}>Cancel</button>
              <button className="sb-modal-save" onClick={handleUpdate} disabled={editLoading}>
                {editLoading ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
